import React, { useState, useEffect } from 'react';
import { Button, Select } from '@/components/ui';
import type { Milestone, MilestoneType } from '@/types';

interface MilestoneFormProps {
  initialData?: Milestone;
  onSubmit: (data: Partial<Milestone>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const MilestoneForm: React.FC<MilestoneFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    targetDate: '',
    completionPercentage: 0,
    description: '',
    type: 'BUSINESS' as MilestoneType,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        targetDate: initialData.targetDate ? initialData.targetDate.split('T')[0] : '',
        completionPercentage: initialData.completionPercentage || 0,
        description: initialData.description || '',
        type: initialData.type || 'BUSINESS',
      });
    }
  }, [initialData]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    // Target date validation
    if (!formData.targetDate) {
      newErrors.targetDate = 'Target date is required';
    } else {
      const targetDate = new Date(formData.targetDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (targetDate < today && !initialData) {
        newErrors.targetDate = 'Target date should be in the future for new milestones';
      }
    }

    // Completion percentage validation
    if (formData.completionPercentage < 0 || formData.completionPercentage > 100) {
      newErrors.completionPercentage = 'Completion percentage must be between 0 and 100';
    }

    // Description validation
    if (formData.description && formData.description.length > 2000) {
      newErrors.description = 'Description must not exceed 2000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const submitData = {
      ...formData,
      targetDate: new Date(formData.targetDate).toISOString(),
      description: formData.description || undefined,
    };

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Target Date */}
        <div className="space-y-1">
          <label className="block text-sm font-medium text-[var(--text)]">
            Target Date <span className="text-status-error">*</span>
          </label>
          <input
            type="date"
            value={formData.targetDate}
            onChange={(e) => handleInputChange('targetDate', e.target.value)}
            min={!initialData ? new Date().toISOString().split('T')[0] : undefined}
            required
            className="block w-full rounded-md border bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] focus:ring-offset-2 focus:ring-offset-[var(--bg)] border-[var(--border)] hover:border-[var(--accent)]/60"
          />
          {errors.targetDate && (
            <p className="text-xs text-status-error font-medium">{errors.targetDate}</p>
          )}
          <p className="text-sm text-[var(--muted)]">When should this milestone be completed?</p>
        </div>

        {/* Milestone Type */}
        <div className="space-y-1">
          <label className="block text-sm font-medium text-[var(--text)]">
            Type <span className="text-status-error">*</span>
          </label>
          <Select
            value={formData.type}
            onChange={(e) => handleInputChange('type', e.target.value as MilestoneType)}
            options={[
              { value: 'BUSINESS', label: 'Business' },
              { value: 'TECH', label: 'Technical' },
            ]}
            required
          />
          <p className="text-sm text-[var(--muted)]">Business-driven or technical milestone?</p>
        </div>

        {/* Completion Percentage */}
        <div className="space-y-1 md:col-span-2">
          <label className="block text-sm font-medium text-[var(--text)]">
            Completion Percentage
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={formData.completionPercentage}
              onChange={(e) => handleInputChange('completionPercentage', parseInt(e.target.value))}
              className="flex-1 h-2 bg-[var(--surface-muted)] rounded-lg appearance-none cursor-pointer accent-[var(--accent)]"
            />
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="100"
                value={formData.completionPercentage}
                onChange={(e) => handleInputChange('completionPercentage', Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                className="w-20 rounded-md border bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent)] border-[var(--border)]"
              />
              <span className="text-sm font-medium text-[var(--text)]">%</span>
            </div>
          </div>
          {errors.completionPercentage && (
            <p className="text-xs text-status-error font-medium">{errors.completionPercentage}</p>
          )}
          <div className="mt-2">
            <div className="w-full bg-[var(--surface-muted)] rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  formData.completionPercentage === 100
                    ? 'bg-status-success'
                    : formData.completionPercentage >= 75
                    ? 'bg-accent-blue'
                    : formData.completionPercentage >= 50
                    ? 'bg-status-warning'
                    : 'bg-status-error'
                }`}
                style={{ width: `${formData.completionPercentage}%` }}
              />
            </div>
          </div>
          <p className="text-sm text-[var(--muted)]">Manually track progress (0-100%)</p>
        </div>

        {/* Description */}
        <div className="space-y-1 md:col-span-2">
          <label className="block text-sm font-medium text-[var(--text)]">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe what this milestone represents and why it's important..."
            rows={4}
            className="block w-full rounded-md border bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text)] placeholder-[var(--muted)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] focus:ring-offset-2 focus:ring-offset-[var(--bg)] border-[var(--border)] hover:border-[var(--accent)]/60"
          />
          {errors.description && (
            <p className="text-xs text-status-error font-medium">{errors.description}</p>
          )}
          <p className="text-sm text-[var(--muted)]">
            {formData.description.length}/2000 characters
          </p>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--border)]">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isLoading}>
          {isLoading ? (initialData ? 'Updating...' : 'Creating...') : (initialData ? 'Update Milestone' : 'Create Milestone')}
        </Button>
      </div>
    </form>
  );
};

export default MilestoneForm;
